import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  useTheme,
  IconButton,
  Divider,
  AppBar,
  Toolbar,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Footer from "../../common/Footer";
import { ColorModeContext, tokens } from "../../../theme";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CloudDownload,
  DarkModeOutlined,
  Download,
  LightModeOutlined,
  Preview,
} from "@mui/icons-material";
import Card from "../../common/Card";
import Button from "../../common/Button";
import ManufacturerNotificationIVRDownload from "./ManufacturerNotificationIVRDownload";
import {
  downloadDocumentById,
  useValidateManufacturerCredentials,
  validateEmailLink,
} from "../../../redux/slice/manufacturerNotifications/ManufacturerNotificationSlice";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { SearchIVRListRequest } from "../../../redux/slice/ivr/createIVRSlice";
import {
  fetchOrderDocumentsByIds,
  SearchOrder,
} from "../../../redux/slice/order/OrderSlice";
import { decryptAES } from "../../../utils/EncryptorUtils";
import { useNavigation } from "../../../hooks/useNavigation";
import {
  fetchIVRDocuments,
  useIVRDocumentSlice,
} from "../../../redux/slice/ivr/ivrDocumentSlice";
import JSZip from "jszip";
import saveAs from "file-saver";
import { fetchDocumentById } from "../../../redux/slice/practiceSlice";
import {
  CodeTable,
  getCodeTableValuesByTableName,
  useCodetablesSlice,
} from "../../../redux/slice/codetableSlice";
import { downloadBase64File, retrieveDataFromTokenCookie } from "../../../utils/Utility";
import {
  fetchDocumentByIdCommonApiPublic,
  setSingleAlertObj,
} from "../../../redux/slice/commonSlice";
import CustomIconButton from "../../common/CustomIconButton";
import PDFViewer from "../../common/pdf/PDFViewer";
import { useBranding } from "../../../BrandingContext";
import SimpleReleasePopup from "../../common/SimpleReleasePopup";
import { fetchAppVersion } from "../../../redux/slice/userSlice";
import { getDataFromCookies, setDataToCookies } from "../../../utils/StorageUtils";
import { TokenString } from "../../../types/CommonTypes";

const ManufactureNotificationAccess: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = React.useContext(ColorModeContext);
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const location = useLocation();
  const { documentInfo, isLinkExpired } = useValidateManufacturerCredentials();
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } =
    useCodetablesSlice();
  const { ivrDocumentInfoList } = useIVRDocumentSlice();
  const [showPopup, setShowPopup] = useState(false);
  const [ivrNumber, setIvrNumber] = useState("");
  const { isValid, userInfo } = useAuthSlice();
  const [email, setEmail] = useState("");
  const [ivrDocId, setIvrDocId] = useState("");
  const [ivrId, setIvrId] = useState("");
  const [orderDocId, setorderDocId] = useState("");
  const [orderNumber, setorderNumber] = useState("");
  const [tissueTrackingDocId, setTissueTrackingDocId] = useState("");
  const [productReturnDocId, setProductReturnDocId] = useState("");
  const [validateToken, setValidateToken] = useState("");
  const [fileName, setFileName] = useState("");
  const [documentTypes, setDocumentTypes] = useState<CodeTable[]>([]);
  const [showPDF, setShowPDF] = useState<{
    isShow: boolean;
    content: string;
    fileName: string;
  }>({ isShow: false, content: "", fileName: "" });
  const [showReleasePopup, setShowReleasePopup] = useState(false);
  const [releaseNoteShown, setReleaseNoteShown] = useState(false);
  const [backendVersion, setBackendVersion] = useState("");
  const branding = useBranding();

  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    // Parse the URL parameters
    const params = new URLSearchParams(location.search);
    const encryptedIvrNumber = params.get("ivrNumber") || null;
    const encryptedIvrDocId = params.get("ivrDocId") || null;
    const encryptedIvrId = params.get("ivrId") || null;
    const encryptedOrderDocId = params.get("orderDocId") || null;
    const encryptedOrderNumber = params.get("orderNumber") || null;
    const encryptedTissueTrackingDocId =
      params.get("tissueTrackingDocId") || null;
    const encryptedProductReturnDocId =
      params.get("productReturnDocId") || null;
    const encryptedEmail = params.get("email") || null;
    const iv = params.get("iv");
    const salt = params.get("salt");
    setValidateToken(params.get("token") || "");

    // Decrypt the parameters only if both are non-null and non-empty
    if (encryptedIvrNumber != null && encryptedIvrNumber !== "") {
      setIvrNumber(decryptAES(encryptedIvrNumber, iv, salt));
    }
    if (encryptedIvrDocId != null && encryptedIvrDocId !== "") {
      setIvrDocId(decryptAES(encryptedIvrDocId, iv, salt));
    }
    if (encryptedIvrId != null && encryptedIvrId !== "") {
      setIvrId(decryptAES(encryptedIvrId, iv, salt));
    }
    if (encryptedOrderDocId != null && encryptedOrderDocId !== "") {
      setorderDocId(decryptAES(encryptedOrderDocId, iv, salt));
    }
    if (encryptedOrderNumber != null && encryptedOrderNumber !== "") {
      setorderNumber(decryptAES(encryptedOrderNumber, iv, salt));
    }
    if (
      encryptedTissueTrackingDocId != null &&
      encryptedTissueTrackingDocId !== ""
    ) {
      setTissueTrackingDocId(
        decryptAES(encryptedTissueTrackingDocId, iv, salt)
      );
    }
    if (
      encryptedProductReturnDocId != null &&
      encryptedProductReturnDocId !== ""
    ) {
      setProductReturnDocId(decryptAES(encryptedProductReturnDocId, iv, salt));
    }
    if (encryptedEmail != null && encryptedEmail !== "") {
      setEmail(decryptAES(encryptedEmail, iv, salt));
    }
  }, [location.search]);

  useEffect(() => {
    const validateAndNavigate = async () => {
      const res = await dispatch(validateEmailLink(validateToken));
    };
    if (validateToken !== null && validateToken !== "") {
      validateAndNavigate();
    }
  }, [validateToken]);

  useEffect(() => {
    if (isLinkExpired) {
      navigate("/signIn");
    }
  }, [isLinkExpired]);

  useEffect(() => {
    let searchCriteriaOrder: SearchOrder = {
      practiceIds: [],
      manufacturerIds: [],
      // CS-905
      patientFirstName: "",
      patientLastName: "",
      //
      productIds: [],
      providerName: "",
      orderNumber: orderNumber,
      invoiceNumber: "",
      serialNumber: "",
      fromDate: "",
      toDate: "",
      status: [],
      invoiceStatus: [],
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
      downloadState: false,
      fromServiceDate: "",
      toServiceDate: "",
    };
    let searchCriteriaIvr: SearchIVRListRequest = {
      productId: [],
      practiceId: [],
      productCategory: [],
      // CS-905
      patientFirstName: "",
      patientLastName: "",
      //
      verificationStatus: [],
      ivrNumber: ivrNumber,
      fromDate: "",
      toDate: "",
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
    };
    if (
      isValid &&
      userInfo.userName === email &&
      ivrDocId != "" &&
      ivrNumber != ""
    ) {
      navigate("/ivr_list", {
        state: {
          ivrDocId: ivrDocId,
          ivrNumber: ivrNumber,
          ivrListSearchCriteria: searchCriteriaIvr,
        },
      });
    } else if (
      isValid &&
      userInfo.userName === email &&
      orderDocId != "" &&
      orderNumber != ""
    ) {
      navigate("/order_list", {
        state: {
          orderDocId: orderDocId,
          orderNumber: orderNumber,
          orderListSearchCriteria: searchCriteriaOrder,
        },
      });
    } else if (
      isValid &&
      userInfo.userName === email &&
      tissueTrackingDocId != "" &&
      orderNumber != ""
    ) {
      navigate("/order_list", {
        state: {
          tissueTrackingDocId: tissueTrackingDocId,
          orderNumber: orderNumber,
          orderListSearchCriteria: searchCriteriaOrder,
        },
      });
    } else if (
      isValid &&
      userInfo.userName === email &&
      productReturnDocId != "" &&
      orderNumber != ""
    ) {
      navigate("/order_list", {
        state: {
          productReturnDocId: productReturnDocId,
          orderNumber: orderNumber,
          orderListSearchCriteria: searchCriteriaOrder,
        },
      });
    } else {
      setShowPopup(true);
    }
  }, [isValid, ivrDocId, ivrNumber, orderDocId, orderNumber]);

  useEffect(() => {
    if (ivrDocId) {
      setFileName("IVR.pdf");
      if (ivrId) {
        dispatch(fetchIVRDocuments({ ivrId: ivrId, patientId: null }));
      }
    } else if (orderDocId) {
      setFileName("Order.pdf");
    } else if (tissueTrackingDocId) {
      setFileName("Tissue Tracking.pdf");
    } else if (productReturnDocId) setFileName("Product Return.pdf");
  }, [ivrDocId, orderDocId, tissueTrackingDocId, productReturnDocId]);

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const handleActionDownload = async (docId: number) => {
    if (docId) {
      const actionResult = await dispatch(
        fetchDocumentByIdCommonApiPublic(docId)
      );
      const documentData = actionResult.payload;
      if (documentData?.content) {
        downloadBase64File(
          documentData.content,
          documentData.documentName,
          documentData.fileType
        );
      } else {
        dispatch(
          setSingleAlertObj({
            message: "Document not found!",
            alertType: "info",
          })
        );
      }
    }
  };

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const sortedDocumentTypes = [
        ...getCodeTableValuesByTableName(
          allCodeTablesValuesMap,
          "documentType"
        ),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setDocumentTypes(sortedDocumentTypes);
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  //CS-1664 START
  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await dispatch(fetchAppVersion({ skipLoader: true }));
        const serverVersion = response.payload?.applicationVersion;
        
        if (serverVersion) {
          setBackendVersion(serverVersion);
          
          const storedVersion = localStorage.getItem("applicationVersion");
          const releaseNoteSessionKey = "manufacturerNotificationReleaseNoteShown";
          const hasShownInSession = sessionStorage.getItem(releaseNoteSessionKey);
          
          const shouldShowPopup = !releaseNoteShown && (
            (!storedVersion && serverVersion) ||
            !hasShownInSession
          );
          
          const shouldNotShow = storedVersion === serverVersion && hasShownInSession === "true";
          
          if (shouldShowPopup && !shouldNotShow) {
            setShowReleasePopup(true);
          }
        }
      } catch (error) {
        console.error("Error checking version:", error);
      }
    };

    checkVersion();
  }, [dispatch, releaseNoteShown]);

  const handleReloadNow = () => {
    const releaseNoteSessionKey = "manufacturerNotificationReleaseNoteShown";
    const storedVersion = localStorage.getItem("applicationVersion");
    if (backendVersion && (!storedVersion || storedVersion !== backendVersion)) {
      localStorage.setItem("applicationVersion", backendVersion);
    }
    const token = getDataFromCookies("token");
    let data: TokenString = retrieveDataFromTokenCookie(token);
    data.token = "";
    setDataToCookies("token", JSON.stringify(data));
    setReleaseNoteShown(true);
    setShowReleasePopup(false);
    sessionStorage.setItem(releaseNoteSessionKey, "true");
    window.location.reload();
  };
  //CS-1664 END

  const commonDownloadComponent = (file?: string, docId?: number) => {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "5px",
            backgroundColor: colors.grey[900],
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 1.5,
              borderRadius: 2,
              borderStyle: "dotted",
              borderColor: colors.grey[600],
              m: 1,
            }}
          >
            <img
              src={require("../../../assets/download_main.png")}
              alt="IVR PDF"
              style={{ width: 40 }}
            />
          </Box>

          <Box>
            <Typography variant="h5" fontWeight="bold">
              {file}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: colors.grey[500] }}>
              Click the button on the right to download your PDF
            </Typography>
          </Box>
          <Box sx={{ padding: "0.5rem 2rem" }}>
            <Typography color={colors.grey[500]} sx={{ mb: 0.5, ml: 0.5 }}>
              Action
            </Typography>
            <CustomIconButton
              color="info"
              size="small"
              variant="outlined"
              onClick={() => docId && handleActionDownload(docId)}
              tooltipMessage="Download"
              tooltipPlacement="bottom"
            >
              <Download />
            </CustomIconButton>
            <CustomIconButton
              color="info"
              size="small"
              variant="outlined"
              onClick={() => docId && handleActionPreview(docId)}
              tooltipMessage="Preview"
              tooltipPlacement="bottom"
            >
              <Preview />
            </CustomIconButton>
          </Box>
        </Box>
        {ivrDocId &&
          file === "Completed IVR" &&
          !(
            ivrDocumentInfoList &&
            ivrDocumentInfoList.length === 1 &&
            ivrDocumentInfoList[0].documentType === "COMPLETED_IVR"
          ) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: colors.grey[900],
              }}
            >
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ display: "flex", alignItems: "center", ml: 1 }}
              >
                Supporting Documents
              </Typography>
            </Box>
          )}
      </>
    );
  };

  const handleActionPreview = async (documentId: number) => {
    if (documentId) {
      const actionResult = await dispatch(
        fetchOrderDocumentsByIds([documentId])
      );
      const documentData = actionResult.payload;
      if (documentData && documentData.content) {
        setShowPDF({
          isShow: true,
          content: documentData.content,
          fileName: documentData.documentName,
        });
      } else {
        dispatch(
          setSingleAlertObj({
            message: "Requested Document has not found under server location!",
            alertType: "info",
          })
        );
      }
    }
  };

  const downloadAll = async () => {
    const dataSource = ivrDocumentInfoList;
    if (!dataSource) return;

    const zip = new JSZip();
    const documentsFolder = zip.folder("documents");
    if (!documentsFolder) {
      console.error("Failed to create documents folder in the zip archive.");
      return;
    }
    for (const row of dataSource) {
      if (row.documentId) {
        await downloadAndAddToZip(row.documentId, documentsFolder);
      }
    }
    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, "all_documents.zip");
    });
  };

  const downloadAndAddToZip = async (documentId: number, zipFolder: JSZip) => {
    const actionResult = await dispatch(
      fetchDocumentByIdCommonApiPublic(documentId)
    );
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      const byteCharacters = atob(documentData.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      zipFolder.file(documentData.documentName, byteArray, { binary: true });
    } else {
      console.log(`Document with ID ${documentId} not found or is empty.`);
    }
  };

  const hasLongContentList =
    ivrDocumentInfoList && ivrDocumentInfoList.length > 3;

  const mainContent = () => {
    return (
      <Container
        maxWidth="sm"
        sx={{
          minHeight: {
            xs: "calc(100dvh - 56px)",
            sm: `calc(100dvh - ${APPBAR_H}px)`,
          },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 4,
        }}
      >
        <Card
          sx={{
            width: "100%",
            p: 2,
            mb: 2,
            padding: "2rem",
            border: "1px solid",
            borderColor: colors.grey[600],
            borderRadius: "12px",
            backgroundColor: colors.grey[800],
          }}
        >
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <img
              src={require("../../../assets/download_title.png")}
              alt="Excel Icon"
              style={{ width: 35, height: 35, marginRight: "0.5rem" }}
            />
            Download{" "}
            {ivrDocId
              ? `IVR (${ivrNumber})`
              : orderDocId
              ? `Order (${orderNumber})`
              : tissueTrackingDocId
              ? `Tissue Tracking`
              : productReturnDocId
              ? `Product Return`
              : ""}
          </Typography>

          <Divider sx={{ mt: 1, mb: 1 }} />
          <Typography variant="body1" sx={{ marginBottom: "1rem" }}>
            Use the 'Download' button to save the PDF to your device, or click
            'Preview' to view it before downloading.
          </Typography>
          {(orderDocId || tissueTrackingDocId || productReturnDocId) &&
            commonDownloadComponent(
              fileName,
              parseInt(orderDocId || tissueTrackingDocId || productReturnDocId)
            )}
          {ivrDocId &&
            ivrDocumentInfoList?.map((info) => {
              const filteredDocumentType =
                documentTypes?.find(
                  (type) => type.keyName === info.documentType
                )?.displayValue || info.documentType;
              return commonDownloadComponent(
                filteredDocumentType,
                info.documentId
              );
            })}
          {ivrDocId &&
            !(
              ivrDocumentInfoList &&
              ivrDocumentInfoList.length === 1 &&
              ivrDocumentInfoList[0].documentType === "COMPLETED_IVR"
            ) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  padding: "1rem",
                  backgroundColor: colors.grey[900],
                  borderRadius: "8px",
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  color="info"
                  sx={{ padding: "0.5rem 2rem" }}
                  onClick={() => downloadAll()}
                >
                  Download All
                </Button>
              </Box>
            )}
        </Card>
      </Container>
    );
  };

  const APPBAR_H = 64;

  return (
    <>
      <Box>
        <AppBar
          position="fixed"
          elevation={1}
          sx={{
            bgcolor: colors.grey[800],
          }}
        >
          <Toolbar
            sx={{
              minHeight: { xs: 56, sm: APPBAR_H },
              px: 2,
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr", // left spacer | center | right
              alignItems: "center",
            }}
          >
            <Box /> {/* left spacer */}
            {/* Centered logo (swap for an <img> if you have one) */}
            <Box
              sx={{
                justifySelf: "center",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <img
                alt="logo"
                src={
                  theme.palette.mode === "dark"
                    ? branding?.logoWhite
                    : branding?.logo ||
                      require("../../../branding-assets/v3biomed/logo.png")
                }
                style={{
                  height: "80px",
                  objectFit: "contain",
                  maxWidth: "100%",
                }}
              />
            </Box>
            {/* Right side action */}
            <Box sx={{ justifySelf: "end" }}>
              <IconButton onClick={colorMode.toggleColorMode}>
                {theme.palette.mode === "dark" ? (
                  <LightModeOutlined />
                ) : (
                  <DarkModeOutlined />
                )}
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
        <Toolbar sx={{ minHeight: { xs: 56, sm: APPBAR_H } }} />
        {mainContent()}
        <Box
          sx={{ position: "fixed", bottom: 0, left: 0, right: 0, borderTop: 5 }}
        >
          <Footer />
        </Box>
      </Box>

      {showPopup && <ManufacturerNotificationIVRDownload onClose={handleClosePopup} />}
      {showPDF && (
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() =>
            setShowPDF({ isShow: false, content: "", fileName: "" })
          }
          title={showPDF.fileName}
          fileName={showPDF.fileName}
          base64String={showPDF.content || ""}
        />
      )}
      {showReleasePopup && (
        <SimpleReleasePopup
          open={showReleasePopup}
          onReloadNow={handleReloadNow}
        />
      )}
    </>
  );
};

export default ManufactureNotificationAccess;
